// 🔹 Stylesheets
import './Login.scss';

// 🔹 React Core
import React, { useState } from 'react';

// 🔹 Routing
import { useNavigate, Link } from 'react-router-dom';

// 🔹 Components
import { Button } from '../../components/Button/Button';

// 🔹 Store
import { sessionStore } from 'src/store/sessionStore';

import { toast } from 'src/utils/appAlert';
import { isValidEmail } from 'src/utils/validation';

// 🔹 Assets
import { useAuthLogin } from 'src/api/mutations/useAuthLogin';
import { useAuthSocialLogin } from 'src/api/mutations/useAuthSocialLogin';
import {
  useGetOnboardingMasterData,
  fetchOnboardingMasterData,
} from 'src/api/mutations/useGetOnboardingMasterData';
import { fetchOnboardingState } from 'src/api/mutations/useGetOnboardingState';
import { computeOnboardingProgress } from 'src/utils/onboardingUtils';
import { queryClient } from 'src/api/queryClient';
import { ReactComponent as RightArrowIcon } from '../../../../assets/images/onboarding-trainer/right-arrow.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/images/onboarding-trainer/info.svg';
import { ReactComponent as EyeClosedIcon } from '../../../../assets/images/onboarding-trainer/eye-closed.svg';
import { ReactComponent as EyeOpenIcon } from '../../../../assets/images/onboarding-trainer/eye-open.svg';
import { ReactComponent as GoogleIcon } from '../../../../assets/images/onboarding-trainer/google.svg';
import { ReactComponent as AppleIcon } from '../../../../assets/images/onboarding-trainer/apple.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/images/onboarding-trainer/logo.svg';

export const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isNavigating, setIsNavigating] = useState(false);
  const setToken = sessionStore((s) => s.setToken);

  const { mutateAsync: login, isPending: isLoginPending } = useAuthLogin();
  const { mutateAsync: socialLogin, isPending: isSocialLoginPending } = useAuthSocialLogin();

  // 🔹 Preload Master Data while user is typing so the onboarding screens load instantly
  useGetOnboardingMasterData();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    if (!email.trim()) newErrors.email = 'Please fill this before continuing';
    else if (!isValidEmail(email)) newErrors.email = 'Please enter a valid email address.';
    if (!password) newErrors.password = 'Please fill this before continuing';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsNavigating(true);
      const user = await login({ email, password, rememberMe });

      // Fetch required data before navigating so it's instantly available
      const [, onboardingStateResult] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['onboarding-master-data'],
          queryFn: fetchOnboardingMasterData,
        }),
        queryClient.fetchQuery({
          queryKey: ['onboarding-state', user.uid],
          queryFn: () => fetchOnboardingState(user.uid),
        }),
      ]);

      setErrors({});
      // Set the session token and the user's profile fetched from backend
      setToken(await user.getIdToken(), {
        ...onboardingStateResult?.profile,
        uid: user.uid,
      });

      // toast.success('Login successful!', {
      //   position: 'top-right',
      //   style: {
      //     borderRadius: '8px',
      //     background: '#0a0b24',
      //     color: '#fff',
      //     border: '1px solid rgba(255, 255, 255, 0.1)',
      //     minWidth: 'max-content',
      //   },
      //   iconTheme: {
      //     primary: '#ffb703',
      //     secondary: '#000',
      //   },
      const activeOnboarding =
        onboardingStateResult?.onboarding ?? onboardingStateResult?.profile?.onboarding;
      const progress = computeOnboardingProgress(activeOnboarding);

      if (progress.isFullyCompleted || progress.isSkipped) {
        navigate('/onboarding-trainer/dashboard');
      } else {
        navigate('/onboarding-trainer');
      }
    } catch (error: any) {
      setIsNavigating(false);
      const newErrors: Record<string, string> = {};

      if (error.code === 'auth/wrong-password') {
        newErrors.password = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-credential') {
        newErrors.password = 'Invalid email or password. Please try again.';
      } else if (error.code === 'auth/user-not-found') {
        newErrors.email = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        newErrors.email = 'Please enter a valid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        newErrors.email = 'Too many failed login attempts. Please try again later.';
      } else if (error.message) {
        // Fallback for custom errors like the email verification one
        const cleanedMessage = error.message
          .replace('Firebase: Error ', '')
          .replace(/\(auth.*\)\./, '')
          .trim();

        if (cleanedMessage.includes('verify your email before logging in')) {
          navigate('/onboarding-trainer/signup/verify-email', {
            state: { email, fromLogin: true },
          });
          return;
        }

        newErrors.email = cleanedMessage;
      }

      setErrors(newErrors);
    }
  };

  const handleSocialLogin = async (providerName: 'google' | 'apple') => {
    try {
      setIsNavigating(true);
      const socialResult = await socialLogin({ providerName, rememberMe });
      const user = socialResult.user;

      // Fetch required data before navigating so it's instantly available
      const [, onboardingStateResult] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['onboarding-master-data'],
          queryFn: fetchOnboardingMasterData,
        }),
        queryClient.fetchQuery({
          queryKey: ['onboarding-state', user.uid],
          queryFn: () => fetchOnboardingState(user.uid),
        }),
      ]);

      const backendProfile = onboardingStateResult?.profile || {};
      let name = backendProfile.name || socialResult.firstName;
      let surname = backendProfile.surname;

      if (
        (!surname || surname === 'Trainer') &&
        socialResult.lastName &&
        socialResult.lastName !== 'Trainer'
      ) {
        surname = socialResult.lastName;
      } else if (!surname) {
        surname = socialResult.lastName || '';
      }

      if (name && name.includes(' ') && (!surname || surname === 'Trainer')) {
        const parts = name.trim().split(/\s+/);
        name = parts[0];
        surname = parts.slice(1).join(' ');
      }

      setToken(await user.getIdToken(), {
        ...backendProfile,
        uid: user.uid,
        name,
        surname,
        avatarUrl:
          backendProfile.avatarUrl ||
          backendProfile.avatar_url ||
          socialResult.avatarUrl ||
          user.photoURL ||
          undefined,
      });

      const activeOnboarding =
        onboardingStateResult?.onboarding ?? onboardingStateResult?.profile?.onboarding;
      const progress = computeOnboardingProgress(activeOnboarding);

      if (progress.isFullyCompleted || progress.isSkipped) {
        navigate('/onboarding-trainer/dashboard');
      } else {
        navigate('/onboarding-trainer');
      }
    } catch (error: any) {
      setIsNavigating(false);
      toast.error(error.message || 'Social login failed');
    }
  };

  return (
    <div className="onboarding-login-container">
      <div className="login-box">
        <div className="login-header">
          <div className="logo-placeholder">{<LogoIcon />}</div>
          <h1 className="login-title">Log in your</h1>
          <p className="login-subtitle">Welcome back! Please enter your details.</p>
        </div>

        <form onSubmit={handleLogin} className="login-form auth-form-common" noValidate>
          <div className="input-group">
            <label>EMAIL</label>
            <input
              type="email"
              name="email"
              autoComplete="email"
              placeholder="john.doe@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: '' });
              }}
              className={errors.email ? 'error-input' : ''}
            />
            {errors.email && (
              <div className="error-message">
                {<InfoIcon />} <span>{errors.email}</span>
              </div>
            )}
          </div>

          <div className="input-group">
            <label>PASSWORD</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: '' });
                }}
                className={errors.password ? 'error-input' : ''}
              />
              <button
                type="button"
                className="eye-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
              </button>
            </div>
            {errors.password && (
              <div className="error-message">
                {<InfoIcon />} <span>{errors.password}</span>
              </div>
            )}
          </div>

          <div className="form-actions">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="checkmark"></span>
              Remember me
            </label>
            <Link to="/onboarding-trainer/forgot-password" className="forgot-password">
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            svg={<RightArrowIcon />}
            className="submit-btn right-arrow"
            isLoading={isLoginPending || isNavigating}
            disabled={isSocialLoginPending || isNavigating}
          >
            Log in
          </Button>

          <p className="privacy-text">
            By continuing you agree with our{' '}
            <strong onClick={() => navigate('/privacy-policy')}>Privacy Policy</strong> and{' '}
            <strong onClick={() => navigate('/terms-of-service')}>Terms & Conditions</strong>
          </p>
        </form>

        <div className="auth-divider">
          <span>OR LOG IN WITH</span>
        </div>

        <div className="auth-social-login">
          <button
            type="button"
            className="social-btn google-btn"
            onClick={() => handleSocialLogin('google')}
            disabled={isLoginPending || isSocialLoginPending || isNavigating}
          >
            {<GoogleIcon />}
            Google
          </button>
          <button
            type="button"
            className="social-btn apple-btn"
            onClick={() => handleSocialLogin('apple')}
            disabled={isLoginPending || isSocialLoginPending || isNavigating}
          >
            {<AppleIcon />}
            Apple
          </button>
        </div>

        <p className="auth-bottom-link">
          Don&apos;t have an account? <Link to="/onboarding-trainer/signup">Sign up</Link>
        </p>
      </div>
      <div className="login-bg-glow"></div>
    </div>
  );
};
